import { SessionModeEnum } from '@protos/learning_management/lms.db_pb';
import {
  NavigateFunction,
  URLSearchParamsInit,
  createSearchParams,
} from 'react-router-dom';
import {
  Active_Project_Details,
  Manage_Student_Projects,
  Project_Resource_View,
} from '../../routeHandling/RoutesNomenclature';

/**
 * `'view'` opens the resource read-only, to look at work that is already
 * stored - from a report, say. The resource page then creates no session and
 * writes nothing back. Anything else is the student working on the project.
 */
export type ProjectResourceViewMode = 'view';

interface ProjectResourceParams {
  resourceId: string;
  projectSessionId?: number | bigint;
  assessmentId?: string;
  demoResource?: boolean;
  sessionMode?: SessionModeEnum;
  viewMode?: ProjectResourceViewMode;
  /** Opened on its own from a resource list, outside the project's sequence. */
  standalone?: boolean;
}

const projectResourceSearchParams = (
  resourceParams: ProjectResourceParams
): URLSearchParamsInit => {
  const {
    resourceId,
    projectSessionId,
    assessmentId,
    demoResource,
    sessionMode,
    viewMode,
    standalone,
  } = resourceParams;

  const params: URLSearchParamsInit = {};

  if (resourceId) {
    params.resourceId = resourceId;
  }
  if (projectSessionId) {
    params.lessonSessionId = projectSessionId.toString();
  }
  if (assessmentId) {
    params.assessmentId = assessmentId;
  }
  if (sessionMode) {
    params.sessionMode = sessionMode.toString();
  }
  if (viewMode) {
    params.viewMode = viewMode;
  }
  if (demoResource) {
    params.demoResource = 'true';
  }
  if (standalone) {
    params.standalone = 'true';
  }

  return params;
};

export const navigateToProjectResource = (
  navigate: NavigateFunction,
  resourceParams: ProjectResourceParams
) => {
  const params = projectResourceSearchParams(resourceParams);

  navigate({
    pathname: Project_Resource_View,
    search: resourceParams.resourceId
      ? `?${createSearchParams(params)}`
      : undefined,
  });
};

/**
 * The same destination `navigateToProjectResource` routes to, as an absolute
 * URL - for links that open the resource in its own tab rather than taking
 * over the current one.
 */
export const projectResourceUrl = (resourceParams: ProjectResourceParams) => {
  const params = projectResourceSearchParams(resourceParams);
  return `${window.location.origin}${Project_Resource_View}?${createSearchParams(
    params
  )}`;
};

export const navigateBackToProjectDetails = (
  navigate: NavigateFunction,
  assessmentId?: string
) => {
  if (assessmentId) {
    navigate({
      pathname: Active_Project_Details,
      search: `?${createSearchParams({ assessmentId })}`,
    });
    return;
  }

  navigate(Manage_Student_Projects);
};
